import dot from 'dot-object';
import { useEffect, useRef, useState } from 'react';
import { cn } from '../../../common/functions/cn';
import useOdinMemo from '../../../common/hooks/use-odin-memo';
import useTestTag from '../../../common/hooks/use-test-tag';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import { forceAssert, getContrastTextColor, isValidHexColor } from '../../common-functions';
import type { DynamicFieldComponentProps, DynamicFieldDataProps } from '../../dynamic-form/dynamic-form.interfaces';
import type { JsonData } from '../../generic-types';
import ErrorLabel from '../../labels/error-label';
import type { CheckboxGroupResult } from '../checkbox-group/checkbox-group.component';

export interface SelectButtonProps
  extends DynamicFieldComponentProps<Array<CheckboxGroupResult>>,
    DynamicFieldDataProps {
  showError?: boolean;
}

const SelectButton = ({
  label,
  value = [],
  data,
  textField,
  valueField,
  inputRef,
  onChange,
  readOnly,
  required,
  error,
  disabled,
  showError = true,
}: SelectButtonProps) => {
  const [selectedValues, setSelectedValues] = useState(value?.map((item) => item.value));
  const contrastColorCache = useRef<Map<string, string>>(new Map());
  const tagCreator = useTestTag(label || 'select-button');

  const optionData = useOdinMemo(
    () =>
      forceAssert<JsonData>(data).map<CheckboxGroupResult>((item) => {
        return {
          value: dot.pick(valueField, item),
          text: dot.pick(textField, item),
          color: dot.pick('color', item),
        };
      }),
    [data]
  );

  useEffect(() => {
    setSelectedValues(value?.map((item) => item.value));
  }, [value]);

  const toggleValue = (option: CheckboxGroupResult) => {
    const currentValues = selectedValues ?? [];
    const isSelected = currentValues.indexOf(option.value) > -1;
    const newValues = isSelected
      ? currentValues.filter((value) => value !== option.value)
      : [...currentValues, option.value];

    const selectedObjects = optionData.filter((item) => newValues.indexOf(item.value) > -1);

    onChange?.(selectedObjects);
    setSelectedValues(selectedObjects.map((item) => item.value));
  };

  const commonButtonStyles = cn(
    'mr-4 min-w-5 justify-center rounded-md leading-5 transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-ring/50 sm:w-auto',
    'h-9 px-2.5 py-1.25 text-base md:h-8 md:text-sm'
  );

  const renderOption = (option: CheckboxGroupResult) => {
    const optionText = option.text;
    const optionColor = option.color;
    const isValidColor = optionColor && isValidHexColor(optionColor);
    const isSelected = (selectedValues?.indexOf(option.value) ?? -1) > -1;
    const hasCustomSelectedBg = isSelected && isValidColor;

    //different styles depending on selected or not
    const buttonStyles = cn(
      commonButtonStyles,
      !isSelected && 'bg-border',
      isSelected && 'text-background',
      isSelected && !hasCustomSelectedBg && 'bg-primary',
      (readOnly || disabled) && 'opacity-70'
    );

    let contrastColor: string | undefined;
    if (hasCustomSelectedBg) {
      const cached = contrastColorCache.current.get(optionColor);
      if (cached) {
        contrastColor = cached;
      } else {
        contrastColor = getContrastTextColor(optionColor);
        contrastColorCache.current.set(optionColor, contrastColor);
      }
    }

    const style = hasCustomSelectedBg ? { backgroundColor: optionColor, color: contrastColor } : undefined;

    return (
      <button
        key={String(option.value)}
        type="button"
        className={buttonStyles}
        style={style}
        aria-pressed={isSelected}
        disabled={readOnly || disabled}
        onClick={() => toggleValue(option)}
        ref={tagCreator(optionText?.replace(/\s+/g, '-').toLowerCase())}
      >
        {optionText}
      </button>
    );
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <RequiredIndicator readOnly={readOnly} required={required} />
        <div role="group" aria-label={label} className="flex flex-col space-y-2" ref={(element) => inputRef?.(element)}>
          {optionData.map(renderOption)}
        </div>
      </div>
      {error && showError && <ErrorLabel>{error}</ErrorLabel>}
    </>
  );
};

export default SelectButton;
